import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartAddEvent, CartErrorEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';

/**
 * @typedef {Object} AddAllToCartRefs
 * @property {HTMLButtonElement} addAllButton - The "Add all to cart" control
 * @property {HTMLElement} liveRegion - Where the outcome is announced
 */

/**
 * Adds every add-on in the block to the cart in one request.
 *
 * Each row already carries its own single-variant form, so this only exists for
 * the bulk control: /cart/add accepts an `items` array, which one form cannot
 * express. The event it dispatches is the same one a single add fires, so the
 * cart drawer and bubble update through their existing listeners.
 *
 * @extends {Component<AddAllToCartRefs>}
 */
export class ProductAddonsComponent extends Component {
  requiredRefs = ['addAllButton', 'liveRegion'];

  connectedCallback() {
    super.connectedCallback();
    this.refs.addAllButton.addEventListener('click', this.#handleClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.addAllButton.removeEventListener('click', this.#handleClick);
  }

  /** @returns {string[]} The variant id of every add-on row that can be bought. */
  get #variantIds() {
    const inputs = /** @type {NodeListOf<HTMLInputElement>} */ (
      this.querySelectorAll('[data-addon-variant-id]:not([data-addon-unavailable])')
    );

    return Array.from(inputs, (input) => input.value).filter(Boolean);
  }

  /** @param {MouseEvent} event */
  #handleClick = async (event) => {
    const { addAllButton, liveRegion } = this.refs;
    const ids = this.#variantIds;

    if (!ids.length) return;

    addAllButton.disabled = true;

    const body = new FormData();
    ids.forEach((id, index) => {
      body.append(`items[${index}][id]`, id);
      body.append(`items[${index}][quantity]`, '1');
    });

    // The cart drawer re-renders from the sections it names, so ask for them in the same round trip
    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'), (item) =>
      item instanceof HTMLElement ? item.dataset.sectionId : null,
    ).filter(Boolean);

    if (sectionIds.length) body.append('sections', sectionIds.join(','));

    const config = fetchConfig('javascript', { body });

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        ...config,
        headers: { ...config.headers, Accept: 'text/html' },
      });
      const result = await response.json();

      if (result.status) {
        liveRegion.textContent = result.description || result.message;
        this.dispatchEvent(new CartErrorEvent(this.id, result.message, result.description, result.errors));
        return;
      }

      liveRegion.textContent = Theme.translations.added ?? '';

      this.dispatchEvent(
        new CartAddEvent({}, this.id, {
          source: 'product-addons-component',
          itemCount: ids.length,
          sections: result.sections,
        }),
      );
    } catch (error) {
      console.error(error);
    } finally {
      addAllButton.disabled = false;
      cartPerformance.measureFromEvent('add:user-action', event);
    }
  };
}

if (!customElements.get('product-addons-component')) {
  customElements.define('product-addons-component', ProductAddonsComponent);
}
